import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  client,
  type CreateGameDto,
  GAME_IMAGE_ORIENTATION,
  type GameEntity,
  type GameLocaleSettingsEntity,
  type GamesRedirectEntity,
  getErrorMessage,
  type UpdateGameDto,
} from '@/api';
import { useLocales } from '@/hooks/useLocales';
import { parseJsonObjectSafe } from '@/lib/json';
import { postProcessForm } from '@/lib/form-postprocess';
import { getFullImageUrl } from '@/lib/images';
import { useGameImageStore } from '@/store/game-image';
import { IMAGE_TYPE } from '@/constants/image-type';
import { ElMessage } from 'element-plus';
import { REDIRECT_REASON_UPSERT_API_OPTIONS } from "@/api/generated-client/models/REDIRECT_REASON_UPSERT_API_OPTIONS";
import { REDIRECT_REASON } from "@/api/generated-client/models/GamesRedirectEntity";

export const useGameEditStore = defineStore('gameEdit', () => {
  const game = ref<GameEntity | undefined>(undefined);
  const loading = ref(false);
  const gameLoadingError = ref<unknown>(undefined);

  const {
    locales,
    isLocalesLoading,
    localesError,
    selectedLocaleId,
    selectLocale,
    defaultLocaleId,
  } = useLocales();

  const gameImageStore = useGameImageStore();

  const loadGame = async (id: string) => {
    if (!id) return;
    loading.value = true;
    gameLoadingError.value = undefined;
    try {
      game.value = await client.games.getOneGame({ id, join: ['localeSettings', 'redirectGame'] });
    } catch (err) {
      gameLoadingError.value = err;
    } finally {
      loading.value = false;
    }
  };

  const gameInitialData = computed<CreateGameDto | undefined>(() => {
    if (game.value === undefined) return undefined;

    const {
      id: gameId,
      nameEn,
      nameRu,
      provider,
      gameOwnerName,
      hasDemo,
      hasMobileVersion,
      lang,
      rtp,
    } = game.value;

    const providerData = parseJsonObjectSafe<
    Omit<CreateGameDto, 'gameId' | 'hidden' | 'redirectGameId' | 'isRedirected' | keyof GameEntity>
    >(game.value.providerData);

    let settings = game.value.localeSettings?.find((ls) => ls.localeId === selectedLocaleId.value);
    if (settings === undefined) {
      settings = game.value.localeSettings?.find((ls) => ls.localeId === defaultLocaleId);
    }

    const { redirectGame } = game.value;

    function toUpsertApiRedirectReason(redirect?: GamesRedirectEntity): REDIRECT_REASON_UPSERT_API_OPTIONS | null {
      if (redirect === undefined || redirect === null) {
        // redirect is not specified
        return null;
      }

      switch (redirect.reason) {
        case null:
          // redirect is specified without a particular reason. means always
          return REDIRECT_REASON_UPSERT_API_OPTIONS.DEFAULT;
        case REDIRECT_REASON.CURRENCY_CRYPTO:
          return REDIRECT_REASON_UPSERT_API_OPTIONS.CURRENCY_CRYPTO;
        case REDIRECT_REASON.CURRENCY_FIAT:
          return REDIRECT_REASON_UPSERT_API_OPTIONS.CURRENCY_FIAT;
        default:
          return null;
      }
    }

    const redirectUpsertApiReason = toUpsertApiRedirectReason(redirectGame);

    return {
      ...providerData,
      gameId,
      nameEn,
      nameRu: nameRu ?? '',
      provider,
      gameOwnerName,
      hasDemo,
      hasMobileVersion,
      lang,
      hidden: settings?.isHidden ?? false,
      isVip: settings?.isVip ?? false,
      redirectGameId: redirectGame?.redirectGameId ?? null,
      redirectReason: redirectUpsertApiReason ?? REDIRECT_REASON_UPSERT_API_OPTIONS.DEFAULT,
      isRedirected: redirectGame?.isRedirected ?? false,
      rtp,
    };
  });

  const gameInitialImages = computed<Record<string, string>>(() => {
    const settings = game.value?.localeSettings?.find((ls) => ls.localeId === selectedLocaleId.value);
    if (settings === undefined) return {};
    return {
      imageUrl: getFullImageUrl(settings.imageUrl),
      verticalImageUrl: getFullImageUrl(settings.verticalImageUrl),
      iconUrl: getFullImageUrl(settings.iconUrl),
    } as Record<string, string>;
  });

  type ReadonlyFields = keyof Omit<CreateGameDto, keyof UpdateGameDto>;
  const READONLY_FIELDS: Record<ReadonlyFields, true> = Object.freeze({
    gameId: true,
    provider: true,
    rtp: true,
  });

  const isBusy = computed(() => loading.value);

  const processGame = async (id: string, dto: UpdateGameDto) => {
    const current = gameInitialData.value;
    if (current === undefined) return;

    try {
      const clone = structuredClone(dto);
      if (!clone.lang || clone.lang?.length === 0) clone.lang = null;

      (Object.keys(clone) as (keyof UpdateGameDto)[]).forEach((key) => {
        if (
          clone[key] === undefined
          || Object.prototype.hasOwnProperty.call(current, key) === false
          || READONLY_FIELDS[key as ReadonlyFields] === true
          || current[key as keyof CreateGameDto] === clone[key]
        ) {
          delete clone[key];
        }
      });

      if (Object.keys(clone).length === 0) return;

      clone.localeId = selectedLocaleId.value;

      const res = await client.games.updateGame({
        id,
        requestBody: postProcessForm(clone, { replaceEmptyStrings: true, emptyValueToSet: null }),
      });

      ElMessage.success(`Game "${res.nameEn}" updated`);

      return res;
    } catch (error) {
      ElMessage.error(getErrorMessage(error, 'Failed to update the game'));
    }
  };

  const uploadImage = async (
    g: Pick<GameEntity, 'id' | 'nameEn'>,
    image: File | null | undefined,
    type: IMAGE_TYPE,
    orientation?: GAME_IMAGE_ORIENTATION,
  ) => {
    if (!image) return;

    if (type === IMAGE_TYPE.ICON) {
      return gameImageStore.updateIcon(
        g as GameEntity,
        image,
      );
    } if (type === IMAGE_TYPE.THUMB) {
      return gameImageStore.updateThumbnail(
        g as GameEntity,
        image,
        orientation ?? GAME_IMAGE_ORIENTATION.HORIZONTAL,
      );
    }
  };

  const processImages = async (g: Pick<GameEntity, 'id' | 'nameEn'>, images: Record<string, File>) => {
    if (
      images.imageUrl === undefined
      && images.verticalImageUrl === undefined
      && images.iconUrl === undefined
    ) return;

    // если произошла ошибка при загрузке локалей, то выбрасываем ошибку,
    // так как картинка загружается в рамках выбранной локали
    if (localesError.value) {
      ElMessage.error('Failed to upload the image: available locales are not loaded');
      ElMessage.error(getErrorMessage(localesError.value));
      return;
    }

    const [horizontalRes, verticalRes, iconRes] = await Promise.all([
      uploadImage(
        g,
        images.imageUrl,
        IMAGE_TYPE.THUMB,
        GAME_IMAGE_ORIENTATION.HORIZONTAL,
      ),
      uploadImage(
        g,
        images.verticalImageUrl,
        IMAGE_TYPE.THUMB,
        GAME_IMAGE_ORIENTATION.VERTICAL,
      ),
      uploadImage(
        g,
        images.iconUrl,
        IMAGE_TYPE.ICON,
      ),
    ]);

    let updatedSettings = horizontalRes as (GameLocaleSettingsEntity | null | undefined);

    if (verticalRes !== undefined && verticalRes !== null) {
      updatedSettings = {
        ...(updatedSettings ?? {}),
        ...verticalRes,
        imageUrl: (horizontalRes as any)?.imageUrl ?? null,
        verticalImageUrl: (verticalRes as any).verticalImageUrl,
        iconUrl: (iconRes as any)?.iconUrl ?? null,
      } as GameLocaleSettingsEntity;
    }

    return updatedSettings ?? undefined;
  };

  const updateGame = async (dto: UpdateGameDto, images: Record<string, File>) => {
    if (loading.value === true) {
      ElMessage.info('The game is updating...');
      return;
    }

    const id = game.value?.id;
    if (!id) {
      ElMessage.info('No game to update');
      return;
    }

    loading.value = true;
    try {
      const [updatedGame, updatedImageSettings] = await Promise.all([
        processGame(id, dto),
        processImages({ id, nameEn: game.value?.nameEn ?? id }, images),
      ]);

      const hasImageUpdate = updatedImageSettings !== null && updatedImageSettings !== undefined;
      if (updatedGame === undefined && !hasImageUpdate) {
        ElMessage.info('Nothing to update');
        return;
      }

      if (game.value) {
        let { localeSettings = [] } = game.value;

        if (updatedGame?.localeSettings !== undefined) {
          localeSettings = localeSettings.length > 0 ? localeSettings.map((ls) => {
            const updated = updatedGame.localeSettings!.find((uls) => uls.localeId === ls.localeId);
            if (updated !== undefined) {
              return {
                ...ls,
                isHidden: updated.isHidden,
                isDisabled: updated.isDisabled,
                isVip: updated.isVip,
              } as GameLocaleSettingsEntity;
            }
            return ls;
          }) : updatedGame.localeSettings as GameLocaleSettingsEntity[];
        }

        if (hasImageUpdate && updatedImageSettings !== undefined) {
          localeSettings = localeSettings.length > 0 ? localeSettings.map((ls) => {
            if (ls.localeId === (updatedImageSettings as any).localeId) {
              return {
                ...ls,
                ...(updatedImageSettings as any),
                imageUrl: (updatedImageSettings as any).imageUrl ?? ls.imageUrl,
                verticalImageUrl: (updatedImageSettings as any).verticalImageUrl ?? ls.verticalImageUrl,
                iconUrl: (updatedImageSettings as any).iconUrl ?? ls.iconUrl,
              } as GameLocaleSettingsEntity;
            }
            return ls;
          }) : [updatedImageSettings as GameLocaleSettingsEntity];
        }

        game.value = {
          ...game.value,
          ...updatedGame,
          localeSettings,
        } as GameEntity;
      }
    } finally {
      loading.value = false;
    }
  };

  const onSelectLocale = (localeId: number) => {
    gameImageStore.setLocaleId(localeId);
    selectLocale(localeId);
  };

  const deleteGame = async (id: string) => {
    if (!id) return;
    loading.value = true;
    try {
      await client.games.deleteGame(id);
      ElMessage.success('Game deleted');
    } catch (error) {
      ElMessage.error(getErrorMessage(error, 'Failed to delete the game'));
    } finally {
      loading.value = false;
    }
  };

  return {
    game,
    loading,
    gameLoadingError,

    locales,
    isLocalesLoading,
    localesError,
    selectedLocaleId,
    defaultLocaleId,

    gameInitialData,
    gameInitialImages,
    READONLY_FIELDS,
    isBusy,

    loadGame,
    updateGame,
    onSelectLocale,
    deleteGame,
  };
});
